import { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Square } from 'lucide-react';

interface VoiceRecorderProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
}

export default function VoiceRecorder({ onTranscript, disabled }: VoiceRecorderProps) {
  const [mode, setMode] = useState<'idle' | 'live' | 'recording'>('idle');
  const [duration, setDuration] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Live Speech Recognition (Instant)
  const startLiveSpeech = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Speech recognition not supported');
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      setMode('live');
      setDuration(0);
      timerRef.current = window.setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    };
    
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      if (transcript.trim()) {
        console.log('Live speech:', transcript);
        onTranscript(transcript.trim());
      }
      stopLiveSpeech();
    };
    
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      stopLiveSpeech();
    };
    
    recognition.onend = () => {
      stopLiveSpeech();
    };
    
    recognition.start();
  };

  const stopLiveSpeech = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setMode('idle');
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Record & Upload (Server STT)
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      streamRef.current = stream;
      chunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        console.log(`Recording complete: ${blob.size} bytes`);
        await uploadRecording(blob);
      };

      mediaRecorder.start();
      setMode('recording');
      setDuration(0);
      
      timerRef.current = window.setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Recording failed:', error);
      // Fallback to live speech if recording fails
      startLiveSpeech();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mode === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const uploadRecording = async (audioBlob: Blob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/api/speech-to-text`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const { text } = await response.json();
        if (text && text.trim() && !text.includes('Voice message received')) {
          console.log('Server transcription:', text);
          onTranscript(text.trim());
        } else {
          throw new Error('Server returned fallback message');
        }
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Server transcription failed:', error.message);
      // Fallback to live speech recognition
      console.log('Falling back to live speech recognition...');
      startLiveSpeech();
    } finally {
      setIsTranscribing(false);
      setMode('idle');
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  if (mode === 'idle' && !isTranscribing) {
    return (
      <div className="flex gap-1">
        <button
          onClick={startLiveSpeech}
          disabled={disabled}
          title="Live Speech (Instant)"
          className="p-2 rounded-lg hover:bg-blue-100 disabled:opacity-50 text-blue-600"
        >
          <Mic className="w-5 h-5" />
        </button>
        <button
          onClick={startRecording}
          disabled={disabled}
          title="Record & Upload (Server STT)"
          className="p-2 rounded-lg hover:bg-green-100 disabled:opacity-50 text-green-600"
        >
          <Square className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
      <div className="flex items-center gap-2 min-w-[60px]">
        <div className={`w-2 h-2 rounded-full ${
          mode === 'live' ? 'bg-blue-500 animate-pulse' :
          mode === 'recording' ? 'bg-red-500 animate-pulse' : 
          isTranscribing ? 'bg-yellow-500 animate-pulse' : 'bg-gray-400'
        }`} />
        <span className="text-sm font-mono">{formatTime(duration)}</span>
      </div>

      <span className="text-xs text-gray-500">
        {mode === 'live' ? 'Listening...' : 
         mode === 'recording' ? 'Recording...' : 
         isTranscribing ? 'Processing...' : ''}
      </span>

      <button
        onClick={mode === 'live' ? stopLiveSpeech : stopRecording}
        title="Stop"
        className="p-1 rounded hover:bg-red-200 text-red-600"
      >
        <MicOff className="w-4 h-4" />
      </button>
    </div>
  );
}